import React, { useState, useEffect } from 'react';

const UtilCharge = () => {
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [actionType, setActionType] = useState('Cargar');
  const [fetchedData, setFetchedData] = useState([]);
  const [file, setFile] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [payloadName, setPayloadName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('token');

        if (!token) {
          setError('No hay token disponible');
          return;
        }

        const response = await fetch(
          process.env.REACT_APP_URL_BASE_BACKEND + '/organizations',
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Error en la solicitud: ${response.status}`);
        }

        const data = await response.json();
        console.log('Organizaciones recibidas:', data);
        setOrganizations(data);

        if (data.length === 1) {
          setSelectedOrg(data[0].name);
        }
      } catch (error) {
        console.error('Error al obtener organizaciones:', error);
        setError('Error al obtener organizaciones: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  useEffect(() => {
    const fetchPayloads = async () => {
      if (actionType === 'Visualizar' && selectedOrg) {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(
            process.env.REACT_APP_URL_BASE_BACKEND +
              `/get-payloads/${selectedOrg}`,
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );

          if (!response.ok) {
            throw new Error(`Error al obtener datos: ${response.status}`);
          }

          const result = await response.json();
          setFetchedData(result.data || []);
        } catch (error) {
          console.error('Error al obtener las cargas útiles:', error);
          setFetchedData([]);
        }
      }
    };

    fetchPayloads();
  }, [actionType, selectedOrg]);

  const handleFileUpload = event => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;
    setFile(selectedFile);
  };

  const handleDownload = async item => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('No se encontró el token de autenticación');
      }

      const response = await fetch(
        process.env.REACT_APP_URL_BASE_BACKEND +
          `/download-payload/${item._id}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(
          `Error en la descarga: ${response.status} - ${errorData}`
        );
      }

      const contentType = response.headers.get('content-type');
      if (
        !contentType ||
        !contentType.includes(
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        )
      ) {
        throw new Error('El archivo descargado no es un archivo Excel válido');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.name || 'carga_util'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url); // Liberar memoria
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error detallado en la descarga:', error);
      alert(`Error al descargar el archivo: ${error.message}`);
    }
  };

  const sendDataToBackend = async () => {
    try {
      if (!file || !selectedOrg || !payloadName) {
        setError('Por favor complete todos los campos requeridos');
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('action', actionType);
      formData.append('organization', selectedOrg);
      formData.append('payloadName', payloadName);

      const token = localStorage.getItem('token');
      const response = await fetch(
        process.env.REACT_APP_URL_BASE_BACKEND + '/upload-excel',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) throw new Error('Error al enviar datos');

      const result = await response.json();
      if (result.success) {
        alert('Archivo cargado exitosamente');
        setPayloadName('');
        setFile(null);
      }
    } catch (error) {
      console.error('Error al enviar datos:', error);
      setError('Error al cargar el archivo');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h2 className="text-2xl font-semibold text-base-content">Carga Util</h2>
      {loading && (
        <div className="flex justify-center items-center p-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Selector de Organización */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Organización
          </label>
          <select
            className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            value={selectedOrg}
            onChange={e => setSelectedOrg(e.target.value)}
          >
            <option value="">Selecciona una organización</option>
            {organizations.map(org => (
              <option key={org._id} value={org.name}>
                {org.name}
              </option>
            ))}
          </select>
        </div>

        {/* Botones de Acción */}
        <div className="flex flex-wrap gap-2">
          {['Cargar', 'Eliminar', 'Visualizar'].map(action => (
            <button
              key={action}
              className={`px-4 py-2 rounded-md transition-colors ${
                actionType === action
                  ? 'bg-accent text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setActionType(action)}
            >
              {action}
            </button>
          ))}
        </div>

        {/* Formulario de Carga */}
        {actionType !== 'Visualizar' && selectedOrg && (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900">
              {actionType} Archivo
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre de la carga útil
              </label>
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="Nombre de la carga útil"
                value={payloadName}
                onChange={e => setPayloadName(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seleccionar archivo
              </label>
              <input
                className="w-full p-2 border border-gray-300 rounded-md bg-white"
                type="file"
                accept=".xls,.xlsx"
                onChange={handleFileUpload}
              />
            </div>

            <button
              className={`btn btn-outline w-full py-2 px-4 rounded-md text-white font-medium ${
                actionType === 'Cargar'
                  ? 'btn-success hover:bg-green-700'
                  : 'btn-error hover:bg-red-700'
              }`}
              onClick={sendDataToBackend}
            >
              {actionType}
            </button>
          </div>
        )}

        {/* Tabla de Visualización */}
        {actionType === 'Visualizar' && fetchedData.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha de Carga
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organización
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fetchedData.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(item.uploadDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.organization}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleDownload(item)}
                        className="text-indigo-600 hover:text-indigo-900 font-medium"
                      >
                        Descargar Excel
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {actionType === 'Visualizar' && fetchedData.length === 0 && (
          <div className="text-center py-4 text-gray-500">
            No hay cargas útiles disponibles para mostrar
          </div>
        )}
      </div>
    </div>
  );
};

export default UtilCharge;
